import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, CircularProgress, Button, Divider, Chip, Card, CardContent, Avatar, Badge
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getUserDetails } from '../services/userService';
import {CheckCircle, Block, Email, Phone, Person, DriveEta, AccessTime, SupportAgent} from '@mui/icons-material';

// Define a robust interface for User data
interface UserDetailType {
    id: number;
    firebaseUid: string;
    name?: string;
    email?: string;
    phone: string;
    role: string;
    gender?: string;
    suspended: boolean;
    phoneVerified: boolean;
    emailVerified: boolean;
    createdAt?: string;
    updatedAt?: string;
    // Optional relation if user is also a driver
    driver?: {
        id: number;
        status?: string;
        vehicleLicensePlateNumber?: string;
    };
}

const UserDetail: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState<UserDetailType | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchUser = async () => {
            if (!id) return;
            try {
                const res = await getUserDetails(Number(id));
                setUser(res.data);
            } catch (err) {
                console.error("Failed to fetch user details", err);
            } finally {
                setLoading(false);
            }
        };
        fetchUser();
    }, [id]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    if (!user) return <Box sx={{ p: 3 }}><Typography>User not found</Typography></Box>;

    const InfoRow = ({ label, value, icon }: { label: string, value: any, icon?: React.ReactNode }) => (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {icon}
                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 500 }}>
                    {label}
                </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 400 }}>
                {value || <span style={{ color: '#aaa' }}>-</span>}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
            {/* Header Navigation */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/users')}
                sx={{ mb: 2, textTransform: 'none' }}
            >
                Back to User List
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                        sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: '1.5rem' }}
                    >
                        {user.name ? user.name.charAt(0).toUpperCase() : <Person />}
                    </Avatar>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            {user.name || "Unknown Name"}
                            <Chip label={`#${user.id}`} size="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                            UID: {user.firebaseUid}
                        </Typography>
                    </Box>
                </Box>
                <Chip
                    label={user.role}
                    color={user.role === 'admin' ? 'error' : 'info'}
                    variant="filled"
                    sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
                />
            </Box>

            {/* Main Layout - Using Flexbox with 66/33 split */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>

                {/* LEFT COLUMN: Main Info (66%) */}
                <Box sx={{ flex: { md: 2 }, minWidth: 0 }}>

                    {/* 1. Profile Information */}
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Badge color="primary" variant="dot"><Person color="action" /></Badge>
                            Profile Information
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        {/* 2-column Grid */}
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 4
                        }}>
                            <Box>
                                <InfoRow label="Full Name" value={user.name} />
                                <InfoRow label="Gender" value={user.gender} />
                                <InfoRow label="Firebase UID" value={user.firebaseUid} />
                            </Box>
                            <Box>
                                <InfoRow label="Email" value={user.email} icon={<Email fontSize="small" color="action"/>} />
                                <InfoRow label="Phone" value={user.phone} icon={<Phone fontSize="small" color="action"/>} />
                                <InfoRow label="Registered On" value={user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'} icon={<AccessTime fontSize="small" color="action"/>} />
                            </Box>
                        </Box>
                    </Paper>

                    {/* 2. Account Status */}
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>Account Status</Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(200px, 1fr))' },
                            gap: 2
                        }}>
                            <Chip
                                icon={user.suspended ? <Block /> : <CheckCircle />}
                                label={user.suspended ? "Status: Suspended" : "Status: Active"}
                                color={user.suspended ? "error" : "success"}
                                sx={{ justifyContent: 'flex-start', py: 2.5, px: 1, fontSize: '0.9rem' }}
                            />
                            <Chip
                                icon={user.emailVerified ? <CheckCircle /> : <Block />}
                                label={user.emailVerified ? "Email Verified" : "Email Unverified"}
                                color={user.emailVerified ? "success" : "warning"}
                                variant="outlined"
                                sx={{ justifyContent: 'flex-start', py: 2.5, px: 1, fontSize: '0.9rem' }}
                            />
                            <Chip
                                icon={user.phoneVerified ? <CheckCircle /> : <Block />}
                                label={user.phoneVerified ? "Phone Verified" : "Phone Unverified"}
                                color={user.phoneVerified ? "success" : "warning"}
                                variant="outlined"
                                sx={{ justifyContent: 'flex-start', py: 2.5, px: 1, fontSize: '0.9rem' }}
                            />
                        </Box>
                    </Paper>
                </Box>

                {/* RIGHT COLUMN: Actions & Relations (33%) */}
                <Box sx={{ flex: { md: 1 }, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Linked Driver Profile (Conditional) */}
                    {user.driver ? (
                        <Card variant="outlined" sx={{ borderRadius: 2, borderTop: '4px solid #ed6c02' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <DriveEta color="warning" />
                                    <Typography variant="h6">Driver Profile</Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="body2" paragraph>
                                    This user is a registered driver.
                                </Typography>
                                <InfoRow label="Driver ID" value={`#${user.driver.id}`} />
                                <InfoRow label="Status" value={user.driver.status} />

                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="warning"
                                    onClick={() => navigate(`/driver/${user.driver?.id}`)}
                                    sx={{ mt: 1 }}
                                >
                                    Go to Driver Profile
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="h6" color="textSecondary" gutterBottom>Driver Profile</Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="body2" color="textSecondary">
                                    This user is not registered as a driver.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

                    {/* Activity Shortcuts */}
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Activity Shortcuts</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Button
                                fullWidth
                                variant="outlined"
                                sx={{ mb: 1.5 }}
                                onClick={() => navigate(`/rides?riderUid=${user.firebaseUid}`)}
                            >
                                View Ride History
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => navigate(`/wallet-transactions?riderUid=${user.firebaseUid}`)}
                            >
                                View Wallet
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Support Card */}
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <SupportAgent color="secondary" />
                                <Typography variant="h6">Support</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="body2" paragraph color="textSecondary">
                                Check for any tickets raised for this User.
                            </Typography>
                            <Button
                                fullWidth
                                variant="contained"
                                color="secondary"
                                onClick={() => navigate(`/support-tickets?userId=${user.firebaseUid}`)}
                            >
                                Check Support Tickets
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
};

export default UserDetail;